// Endpoint returning a fresh CSRF token via Django's `get_token` (backed by the
// session secret under CSRF_USE_SESSIONS).
export const CSRF_REFRESH_URL = "/events/csrf/";

/**
 * Write the freshly-issued token into <meta name="csrf-token">. This meta is the
 * single source of truth that `getCookie("csrftoken")` reads first, so updating
 * it here makes the refresh self-healing: without it the meta would keep serving
 * the stale token and every request would 403 → refresh → retry forever.
 */
function updateCsrfMeta(token: string): void {
    if (typeof document === "undefined") {
        return;
    }
    const existing = document.querySelector('meta[name="csrf-token"]');
    if (existing) {
        existing.setAttribute("content", token);
        return;
    }
    // Build the element fully before inserting it, so it never sits in the DOM
    // without its content value.
    const meta = document.createElement("meta");
    meta.setAttribute("name", "csrf-token");
    meta.setAttribute("content", token);
    document.head.appendChild(meta);
}

/**
 * Why a refresh produced no token.
 *
 * The distinction is load-bearing for the caller's remedy, so it cannot collapse to null:
 * `unauthenticated` is the server saying the session is gone, and only that justifies sending
 * someone to log in. `unavailable` covers a 5xx, a dropped connection, an abort, or a malformed
 * body — the session may well be fine, and telling that user they have been signed out would
 * strand a draft over a transient blip.
 */
export type CsrfRefreshFailure = "unauthenticated" | "unavailable";

export type CsrfRefreshResult = { token: string } | { token: null; failure: CsrfRefreshFailure };

/**
 * Fetch a fresh CSRF token and update <meta name="csrf-token"> so every later
 * `getCookie("csrftoken")` read returns the current token, reporting *why* on failure.
 *
 * The endpoint requires a live session and answers 401 when there is none, which is the one
 * response that means the session itself is over.
 */
export async function refreshCsrfTokenResult(signal?: AbortSignal): Promise<CsrfRefreshResult> {
    let response: Response;
    try {
        response = await fetch(CSRF_REFRESH_URL, {
            method: "GET",
            credentials: "same-origin",
            signal,
        });
    } catch {
        return { token: null, failure: "unavailable" };
    }

    // Only 401 means the session is gone: that is the one status the endpoint itself returns
    // for a missing session, and its GET is CSRF-exempt, so a 403 here can only be a WAF/edge
    // block -- an outage, not a logout, and it must not route anyone to the login path.
    if (response.status === 401) {
        return { token: null, failure: "unauthenticated" };
    }
    if (!response.ok) {
        return { token: null, failure: "unavailable" };
    }

    try {
        const data: Record<string, unknown> = await response.json();
        const token = typeof data.csrfToken === "string" && data.csrfToken ? data.csrfToken : null;
        if (!token) {
            return { token: null, failure: "unavailable" };
        }
        updateCsrfMeta(token);
        return { token };
    } catch {
        return { token: null, failure: "unavailable" };
    }
}

/**
 * Used to recover from a stale/mismatched token — after the session's CSRF secret rotates
 * (login/masquerade) or in embedded webviews. Returns null on any failure so callers fall
 * through to normal error handling; reach for `refreshCsrfTokenResult` when the reason matters.
 */
export async function refreshCsrfToken(signal?: AbortSignal): Promise<string | null> {
    return (await refreshCsrfTokenResult(signal)).token;
}
